<template>
  <q-select
    ref="select"
    :class="[variant === 'outlined' ? 'outlined text-sm' : 'text-sm']"
    :menu-offset="variant === 'outlined' ? [0, 0] : [0, 0]"
    :popup-content-class="`${variant} p-select`"
    :popup-content-style="popupContentStyle"
    bg-color="neutral-10"
    dropdown-icon="sym_r_expand_more"
    label-color="neutral-80"
    options-selected-class="bg-primary-40 text-neutral"
    emit-value
    map-options
    outlined
    @popup-show="onPopupShow"
    @update:model-value="onUpdateModelValue"
  >
    <template #option="{ itemProps, opt, selected }">
      <q-item
        class="text-sm q-py-xs"
        :class="{ 'bg-neutral-10 text-neutral': !selected }"
        v-bind="itemProps"
      >
        <q-item-section>
          <q-item-label
            :class="opt?.labelClass"
            :title="opt[$attrs['option-label'] || 'label'] || opt"
          >
            {{ opt[$attrs["option-label"] || "label"] ?? opt }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>

<script setup>
import { colors } from "quasar";

const { getPaletteColor } = colors;

const props = defineProps({
  optionsSelectedColor: {
    type: String,
    default: "neutral",
  },
  variant: {
    type: String,
    default: "",
  },
});

const optionsSelectedColor = computed(() =>
  getPaletteColor(props.optionsSelectedColor),
);

const select = ref("select");

const onUpdateModelValue = () => {
  select?.value?.updateInputValue("");
};

let popupContentStyle = ref({
  width: "150px",
});

const onPopupShow = () => {
  unref(popupContentStyle).width = `calc(${
    unref(select).$el.offsetWidth
  }px - 37px) !important`;
};
</script>

<style lang="scss">
.p-select {
  box-shadow: $elevation-3;

  .q-virtual-scroll {
    &__content {
      .q-item {
        .q-focus-helper {
          display: none;
        }

        &.disabled {
          color: $neutral-70;
          background-color: $neutral-30;

          .text-caption {
            color: $neutral-60;

            * {
              color: $neutral-60;
            }
          }
        }

        &:not(&--active, .disabled) {
          &:hover,
          &.q-manual-focusable--focused {
            background-color: $primary-30;
          }
        }
      }
    }
  }

  &.outlined {
    border: 2px solid $primary;
    border-width: 0 2px 2px 2px;
    border-radius: 0 0 12px 12px;

    box-shadow: none;
  }
}
</style>

<style lang="scss" scoped>
.q-select {
  width: 150px;

  :deep(.q-field__inner) {
    .q-field {
      &__control {
        min-height: 38px;

        &::before {
          border-color: $neutral-50;
        }

        &:not(.text-negative):hover::after {
          border-color: $primary-80;
        }

        &:not(.text-negative):focus-within::after {
          border-color: $primary;
        }

        &-container {
          max-width: 200px;
        }
      }

      &__marginal {
        height: 38px;
      }

      &__native {
        color: v-bind("optionsSelectedColor");

        min-height: 38px;

        .q-chip {
          background: $neutral-40;
          color: $neutral;
        }
      }

      &__input {
        width: 115px;
        &::placeholder {
          color: $neutral-60;
        }
      }
    }
  }

  &--dense {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          min-height: 30px;
        }

        &__marginal {
          height: 30px;
        }

        &__native {
          min-height: 30px;
        }
      }
    }
  }

  &--disabled {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          background-color: $neutral-30;
        }
      }
    }
  }

  &:not(&--multiple) {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          height: 38px;
        }

        &__native {
          height: 38px;

          span {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
          }
        }
      }
    }
  }

  &.outlined {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          transition:
            border,
            300ms linear;

          border: 2px solid transparent;
          border-width: 2px 2px 0 2px;
          border-radius: 12px 12px 0 0;

          &::after {
            display: none;
          }
        }
      }
    }

    &:has([aria-expanded="true"]) {
      :deep(.q-field__inner) {
        .q-field {
          &__control {
            border: 2px solid $primary;
            border-width: 2px 2px 0 2px;
          }
        }
      }
    }
  }
}
</style>
